import {useCallback, useMemo, useState} from 'react';
// @ts-expect-error
import {useWatch} from 'react-hook-form';
import {useSelector} from 'react-redux';

import {FieldMapping} from '../../../editor/mapping/field-mapping';

import type {FormFieldSchema, FormSchemaProperty} from '../../../../../core/nutshell-fields/types';
import {getFormSchema} from '../../../../forms-selectors';
import type {FormFieldRowForDuplicateHelpers} from '../../../../types';

import type {Field, FormPage, InputFormField} from '../../types';

import type {SingleChoiceFieldPresentation} from '../add-custom-field/consts';
import {isFormFieldInputRow} from '../add-custom-field/helpers';
import type {CustomFieldView} from '../blocks-and-design/content-blocks/types';

import {EditCustomFieldPanel} from './edit-custom-field-panel';
import {
    editCustomFieldPanelTitle,
    editedFieldRowPathPrefix,
    getSingleChoiceFormFieldTypePresentation,
    isFormPageInputField,
} from './helpers';
import type {EditCustomFieldProps} from './types';

export type {EditCustomFieldProps, EditCustomFieldPanelProps} from './types';

export function EditCustomField(props: EditCustomFieldProps) {
    const {setValue, getValues} = props;
    const {view, existingField, editedFieldRef} = props.session;

    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

    const prefix = editedFieldRowPathPrefix(editedFieldRef);

    const formSchema = useSelector(getFormSchema) as FormFieldSchema | undefined;

    const row = useWatch({
        control: props.control,
        name: prefix,
    });

    const pages = useWatch({
        control: props.control,
        name: 'pages',
    });

    const singleChoicePresentation = useMemo(() => {
        if (!row || !formSchema) {
            return null;
        }

        return getSingleChoiceFormFieldTypePresentation(row as Field, formSchema);
    }, [row, formSchema]);

    const panelView = useMemo((): CustomFieldView => {
        if (singleChoicePresentation === 'dropdown') {
            return 'dropdown';
        }

        if (singleChoicePresentation === 'radio') {
            return 'radio';
        }

        return view;
    }, [singleChoicePresentation, view]);

    const title = useMemo(() => editCustomFieldPanelTitle(panelView), [panelView]);

    const fieldKey = useMemo(() => {
        if (!row || !isFormFieldInputRow(row)) {
            return '';
        }

        return String(row.attributeId ?? row.fieldName ?? '');
    }, [row]);

    const fieldSchemaProp = useMemo((): FormSchemaProperty | undefined => {
        if (!fieldKey || !formSchema?.properties) {
            return undefined;
        }

        return formSchema.properties[fieldKey];
    }, [fieldKey, formSchema]);

    const {otherFieldsOnForm, fieldsForMapping} = useMemo(() => {
        const otherInput: FormFieldRowForDuplicateHelpers[] = [];
        const all: FormFieldRowForDuplicateHelpers[] = [];

        const {pageIndex: excludePage, fieldIndex: excludeField} = editedFieldRef;

        pages?.forEach((page: FormPage, pageIndex: number) => {
            page.fields?.forEach((field, fieldIndex: number) => {
                const withKey = field as FormFieldRowForDuplicateHelpers;
                all.push(withKey);
                if (pageIndex === excludePage && fieldIndex === excludeField) {
                    return;
                }
                if (field.type === 'INPUT') {
                    otherInput.push(withKey);
                }
            });
        });

        return {otherFieldsOnForm: otherInput, fieldsForMapping: all};
    }, [pages, editedFieldRef]);

    const choiceDisplayLabels = useMemo(() => {
        if (fieldSchemaProp?.options?.length) {
            return fieldSchemaProp.options.map((opt) => opt.name);
        }
        return existingField.choices;
    }, [fieldSchemaProp, existingField.choices]);

    const setSingleChoiceFieldType = useCallback(
        (next: SingleChoiceFieldPresentation) => {
            setValue(`${prefix}.type`, next === 'radio' ? 'RADIO' : 'INPUT', {
                shouldDirty: true,
                shouldTouch: true,
            });
        },
        [prefix, setValue]
    );

    const toggleRequired = useCallback(() => {
        const {pageIndex, fieldIndex} = editedFieldRef;
        const pages = getValues('pages') as FormPage[] | undefined;
        const page = pages?.[pageIndex];
        const field = page?.fields[fieldIndex];

        if (!page || !isFormPageInputField(field)) {
            return;
        }

        const toggled: InputFormField & {key: string} = {
            ...field,
            isRequired: !field.isRequired,
        };

        const nextFields = [...page.fields];
        nextFields[fieldIndex] = toggled;

        setValue(`pages.${pageIndex}.fields`, nextFields, {shouldDirty: true, shouldTouch: true});
    }, [editedFieldRef, getValues, setValue]);

    return (
        <>
            <EditCustomFieldPanel
                title={title}
                onBack={props.onBack}
                existingField={existingField}
                choiceDisplayLabels={choiceDisplayLabels}
                panelView={panelView}
                showSingleChoiceFieldTypeToggle={singleChoicePresentation != null}
                singleChoicePresentation={singleChoicePresentation}
                setSingleChoiceFieldType={setSingleChoiceFieldType}
                labelValue={String(row?.label ?? '')}
                placeholderValue={String(row?.placeholder ?? '')}
                requiredValue={Boolean(row && isFormFieldInputRow(row) && row.isRequired)}
                setLabel={(next) =>
                    setValue(`${prefix}.label`, next, {shouldDirty: true, shouldTouch: true})
                }
                setPlaceholder={(next) =>
                    setValue(`${prefix}.placeholder`, next, {
                        shouldDirty: true,
                        shouldTouch: true,
                    })
                }
                toggleRequired={toggleRequired}
                isEditModalOpen={isEditModalOpen}
                setIsEditModalOpen={setIsEditModalOpen}
            />
            {formSchema && fieldSchemaProp && row && isFormFieldInputRow(row) && (
                <div className='px-16'>
                    <FieldMapping
                        wrapInCard={false}
                        control={props.control}
                        name={prefix}
                        watch={props.watch}
                        field={row}
                        otherFieldsOnForm={otherFieldsOnForm}
                        formSchema={formSchema}
                        formId={props.formId}
                        preventEditing={!row.isRemovable}
                        fields={fieldsForMapping}
                        onEditNameField={() => {}}
                        onEditEmailField={() => {}}
                        onEditAddressMultiFields={() => {}}
                    />
                </div>
            )}
        </>
    );
}
